import React, { createContext, useContext, useMemo, useReducer, useEffect } from "react";

const CartCtx = createContext(null);

const initial = {
  items: [], // { product, quantity }
  open: false,
};

function reducer(state, action) {
  switch (action.type) {
    case "open":
      return { ...state, open: true };
    case "close":
      return { ...state, open: false };
    case "toggle":
      return { ...state, open: !state.open };
    case "add": {
      const idx = state.items.findIndex((i) => i.product.id === action.product.id);
      if (idx >= 0) {
        const items = [...state.items];
        items[idx] = { ...items[idx], quantity: items[idx].quantity + 1 };
        return { ...state, items, open: true };
      }
      return {
        ...state,
        items: [...state.items, { product: action.product, quantity: 1 }],
        open: true,
      };
    }
    case "remove":
      return { ...state, items: state.items.filter((i) => i.product.id !== action.id) };
    case "qty": {
      const items = state.items
        .map((i) => (i.product.id === action.id ? { ...i, quantity: action.qty } : i))
        .filter((i) => i.quantity > 0);
      return { ...state, items };
    }
    case "hydrate":
      return { ...state, items: action.items || [] };
    case "clear":
      return { ...state, items: [] };
    default:
      return state;
  }
}

const STORAGE_KEY = "cg-cart-v1";

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initial);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) dispatch({ type: "hydrate", items: JSON.parse(raw) });
    } catch (_) {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    } catch (_) {}
  }, [state.items]);

  const value = useMemo(() => {
    const subtotal = state.items.reduce(
      (sum, i) => sum + i.product.price * i.quantity,
      0
    );
    const count = state.items.reduce((s, i) => s + i.quantity, 0);
    return { state, dispatch, subtotal, count };
  }, [state]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
